import queryActionFactory from 'common/actions/queryActionFactory';
import {
  type IdeaActivity,
  IdeaActivityType,
  type IdeaComment,
} from 'common/api/endpoints/ideaActivity';
import DataV2 from 'common/DataV2';
import uniqueByKey from 'common/util/uniqueByKey';

import type { Dispatch, GetState } from 'redux-connect';

export type QueryParams = {
  ideaID: string;
  limit?: number;
  sort?: 'newest' | 'oldest';
};

const {
  // query helpers
  getQueryKey,

  // action creators
  invalidateQueries: invalidateIdeaActivityQueries,
  loadMore: loadMoreIdeaActivity,
  loadQuery: loadIdeaActivity,
  reloadQuery: reloadIdeaActivityQuery,

  // internal actions
  bulkRemoveAction,
  loadedAction,

  // action types
  BulkLoadedType,
  BulkRemoveType,
  ErrorType,
  InvalidateType,
  LoadedMoreType,
  LoadedType,
  RequestMoreType,
  RequestType,
} = queryActionFactory<IdeaActivity, QueryParams>(
  'ideaActivityQueries',
  DataV2.queries.ideaActivity,
);

export {
  getQueryKey,
  invalidateIdeaActivityQueries,
  loadMoreIdeaActivity,
  loadIdeaActivity,
  reloadIdeaActivityQuery,
  BulkLoadedType,
  BulkRemoveType,
  ErrorType,
  InvalidateType,
  LoadedMoreType,
  LoadedType,
  RequestMoreType,
  RequestType,
};

export const upsertIdeaComments = (queryParams: QueryParams, comments: IdeaComment[]) => {
  return (dispatch: Dispatch, getState: GetState): void => {
    const state = getState();
    const queryKey = getQueryKey(queryParams);
    const queryState = state.ideaActivityQueries?.[queryKey];

    if (!queryState?.items) {
      return;
    }

    const activityItems: IdeaActivity[] = comments.map((comment) => ({
      _id: comment._id,
      created: comment.created,
      type: IdeaActivityType.comment,
      comment,
    }));

    let updatedItems = [...queryState.items];

    activityItems.forEach((activityItem) => {
      const existingIndex = updatedItems.findIndex(
        (item: IdeaActivity) => item._id === activityItem._id,
      );

      if (existingIndex !== -1) {
        updatedItems[existingIndex] = activityItem;
      } else {
        updatedItems = [activityItem, ...updatedItems];
      }
    });

    updatedItems = uniqueByKey(updatedItems, '_id');

    dispatch(
      loadedAction(queryKey, queryParams, {
        cursor: queryState.cursor ?? null,
        hasNextPage: queryState.hasNextPage ?? false,
        items: updatedItems,
      }),
    );
  };
};

export const removeIdeaActivities = (queryParams: QueryParams, activityIDs: string[]) => {
  return (dispatch: Dispatch, getState: GetState): void => {
    const state = getState();
    const queryKey = getQueryKey(queryParams);
    const queryState = state.ideaActivityQueries?.[queryKey];

    if (!queryState?.items) {
      return;
    }

    const activityIDsSet = new Set(activityIDs);
    const activitiesToRemove = queryState.items.filter((item: IdeaActivity) =>
      activityIDsSet.has(item._id),
    );

    if (activitiesToRemove.length === 0) {
      return;
    }

    dispatch(
      bulkRemoveAction([
        {
          queryKey,
          queryParams,
          items: activitiesToRemove,
        },
      ]),
    );
  };
};
